// Outlook e-mails on the client timeline (Oct 2026, idea 35) — the CRM side. While the CRM is open and signed in, the
// e-mails the platform found (only those with CRM clients) are recorded through the domain command and then
// acknowledged, so the platform deletes its copy. Per client the advisor may keep the e-mail text (off by default).
import { useEffect, useState } from 'react';
import { keepsOutlookText, outlookMailId, recordOutlookMails, setOutlookMailBodies } from '@vero/domain';
import type { OutlookStatus } from '@vero/platform';
import { useRuntime } from '../core/runtime.tsx';
import { accountService } from '../core/account-service.ts';
import { useAccount } from './account.tsx';

let shared: OutlookStatus | null = null;
const listeners = new Set<(s: OutlookStatus | null) => void>();
const setShared = (s: OutlookStatus | null) => { shared = s; for (const l of listeners) l(s); };
/** the member's Outlook status as last seen by the watcher (null: not connected / not signed in) */
export function useOutlookMail(): OutlookStatus | null {
  const [s, setS] = useState(shared);
  useEffect(() => { listeners.add(setS); return () => { listeners.delete(setS); }; }, []);
  return s;
}

/** runs in the app shell: at start, on focus and every 10 minutes (signed in, online, Outlook mail connected) */
export function OutlookMailWatcher() {
  const rt = useRuntime();
  const a = useAccount();
  useEffect(() => {
    if (!a?.signedIn) { setShared(null); return; }
    let stopped = false, running = false;
    const pull = async () => {
      if (stopped || running || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
      running = true;
      try {
        const api = accountService()?.api;
        if (!api) return;
        const info = await api.info().catch(() => null);
        if (!info?.outlook) { setShared(null); return; }
        const st = await api.outlookStatus();
        setShared(st);
        if (!st.mail || !st.mailPending) return;
        const { items } = await api.outlookMailItems();
        if (!items.length || rt.ws.readOnlyReason) return;
        if (items.some((i) => !rt.ws.view.get('communications', outlookMailId(i.key)) && rt.ws.view.get('persons', i.personId))) {
          await rt.ws.run((v, c) => { const cs = recordOutlookMails(v, items, c); if (!cs) throw new Error('nothing_new'); return cs; }).catch(() => undefined);
        }
        // acknowledged only once recorded: what failed is offered again next time
        const done = items.filter((i) => !!rt.ws.view.get('communications', outlookMailId(i.key)) || !rt.ws.view.get('persons', i.personId)).map((i) => i.key);
        await api.outlookMailAck(done);
        setShared({ ...st, mailPending: 0 });
      } catch { /* offline or signed out: next time */ } finally { running = false; }
    };
    const first = setTimeout(() => void pull(), 2500);
    const every = setInterval(() => void pull(), 10 * 60_000);
    const onFocus = () => void pull();
    window.addEventListener('focus', onFocus);
    return () => { stopped = true; clearTimeout(first); clearInterval(every); window.removeEventListener('focus', onFocus); };
  }, [a?.signedIn]);
  return null;
}

/** on the client's timeline: keep the text of this client's e-mails (only when Outlook e-mail is connected) */
export function OutlookMailToggle({ personId }: { personId: string }) {
  const rt = useRuntime();
  const st = useOutlookMail();
  if (!st?.mail) return null;
  const on = keepsOutlookText(rt.view, personId);
  return (
    <label className="row tiny muted" style={{ gap: 6, padding: '0 16px 10px' }} data-testid="outlook-mail-bodies">
      <input type="checkbox" checked={on} onChange={(e: { target: HTMLInputElement }) => void rt.run((v, c) => setOutlookMailBodies(v, personId, e.target.checked, c), 'common.saved')} />
      {rt.t('outlook.mail.keep_text')}
    </label>
  );
}
